import { Button, ButtonGroup, ColumnHeader, Table } from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import * as React from 'react';
import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../appStore';
import { ToastType } from '../../../components/audience/ToastType';
import ContentConstants from '../../../contentConstants';
import { EmailTemplateType, IColumn, IEmailTemplate } from '../../../models';
import { Status } from '../../../models/Status';
import { SurveyStatusType } from '../../../models/SurveyStatusType';
import { ActionCreators as AudienceDetailsActions, defaultAudience } from '../../../store/AudienceDetailsStore';
import {
	addEmailTemplate,
	addReminderEmailValidationErrors,
	defaultEmailTemplate,
	deleteEmailTemplate,
	getEmailTemplates,
	updateEmailTemplate,
} from '../../../store/EmailTemplateSlice';
import { ColumnHelper } from '../../../utilities/ColumnHelper';
import { ReminderEmailModal } from '../../audience/email/ReminderEmailModal';
import { CardLayout, HeaderButtonContainer, LabelAbove } from '../../common';
import { ReminderRow } from './ReminderRow';

export const defaultColumns: IColumn[] = [
	{
		name: 'Name',
		displayText: 'Name',
		checked: true,
		displayOrder: 0,
		disabled: false,
	},
	{
		name: 'Send Date',
		displayText: 'Send Date',
		checked: true,
		displayOrder: 1,
		disabled: false,
	},
	{
		name: 'Actions',
		displayText: 'Actions',
		checked: true,
		displayOrder: 2,
		disabled: false,
	},
];

export interface IAdminReminderEmailProps {
	surveyId: number;
	audienceId: number;
}

export const AdminReminderEmail = (props: IAdminReminderEmailProps) => {
	const { surveyId, audienceId } = props;
	const dispatch = useAppDispatch();
	const [reminderEmail, setReminderEmail] = useState(defaultEmailTemplate);
	const [isReminderEmailModalOpen, setIsReminderEmailOpen] = useState(false);
	const { audiences, isLoadingAudiences, surveyAdminSummary } = useAppSelector((state) => state.surveyAdmin);
	const { invitationEmail, reminderEmails, reminderEmailValidationErrors, status } = useAppSelector((state) => state.emailTemplates);
	React.useEffect(() => {
		if (audienceId !== 0) {
			dispatch(getEmailTemplates({ surveyId: surveyId, audienceId: audienceId }));
		}
	}, [dispatch, surveyId, audienceId]);

	const toast = (type: ToastType, message: string) => {
		dispatch(AudienceDetailsActions.Toast(type, message));
	};

	const toggleModal = () => {
		let resetReminderEmail = { ...reminderEmail };
		const reminderEmailModalOpen = !isReminderEmailModalOpen;
		if (reminderEmailModalOpen) {
			// when the modal opens, reset properties
			resetReminderEmail = {
				...invitationEmail,
				invitationType: EmailTemplateType.Reminder,
			};
		}
		setReminderEmail(resetReminderEmail);
		setIsReminderEmailOpen(reminderEmailModalOpen);
		dispatch(addReminderEmailValidationErrors());
	};

	const readOnly = surveyAdminSummary ? surveyAdminSummary.surveyStatus === SurveyStatusType.Cancelled : false;
	const audience = _.find(audiences, (a) => a.audienceId === audienceId) ?? defaultAudience;

	return (
		<CardLayout
			loading={isLoadingAudiences || status === Status.Loading}
			title={
				readOnly ? ContentConstants.SURVEY_ADMIN_ACTION_REMINDER_EMAILS_READONLY : ContentConstants.SURVEY_ADMIN_ACTION_REMINDER_EMAILS
			}
			header={
				!readOnly && (
					<HeaderButtonContainer>
						<ButtonGroup type='column'>
							<Button
								id='AddNewReminderButton'
								color='secondary'
								onClick={toggleModal}
								aria-label={ContentConstants.BUTTONS_ADD_NEW_REMINDER}
							>
								{ContentConstants.BUTTONS_ADD_NEW_REMINDER}
							</Button>
						</ButtonGroup>
					</HeaderButtonContainer>
				)
			}
		>
			{readOnly && <LabelAbove type='input' textIsHtml={true} text={ContentConstants.SURVEY_CANCEL_CONTENT_READONLY} />}
			<Table lastalign='right'>
				<ColumnHeader columns={ColumnHelper.GetColumnHeaders(defaultColumns)} />
				<tbody>
					{reminderEmails.map((email: IEmailTemplate) => (
						<ReminderRow
							key={email.emailTemplateId}
							columns={defaultColumns}
							reminderEmail={email}
							audience={audience}
							surveyId={surveyId}
							reminderEmailValidationError={reminderEmailValidationErrors}
							AddReminderEmailValidationErrors={addReminderEmailValidationErrors}
							DeleteEmailTemplate={(emailTemplateId: number) => dispatch(deleteEmailTemplate({ surveyId, audienceId, emailTemplateId }))}
							UpdateEmailTemplate={(emailTemplate: IEmailTemplate) => dispatch(updateEmailTemplate({ surveyId, emailTemplate }))}
							Toast={toast}
							readOnly={readOnly}
						/>
					))}
				</tbody>
			</Table>
			<ReminderEmailModal
				audience={audience}
				reminderEmail={reminderEmail}
				reminderEmails={reminderEmails}
				modalOpen={isReminderEmailModalOpen}
				validationErrors={reminderEmailValidationErrors}
				addReminderToList={(newReminder: IEmailTemplate) => dispatch(addEmailTemplate({ surveyId, emailTemplate: newReminder }))}
				addReminderEmailValidationErrors={addReminderEmailValidationErrors}
				closeModal={toggleModal}
				toast={toast}
				readOnly={readOnly}
			/>
		</CardLayout>
	);
};
